import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Linkedin, Twitter, Mail, Award, Calendar, Globe, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link, useNavigate } from 'react-router-dom';
import { fetchTeam, deleteTeamMember } from '../services/team';
import { team as defaultTeam } from '../data/team';
import { useAuth } from '../contexts/AuthContext';

const Founders = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isFounder = user && user.user_type === 'fondateur';
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [revealed, setRevealed] = useState([]);
  const [tiltTransforms, setTiltTransforms] = useState({});
  const [isDesktop, setIsDesktop] = useState(true);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showList, setShowList] = useState(false);

  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const listRef = useRef(null);
  const heroBgUrl = '';

  // Charger l'équipe depuis l'API
  const loadTeam = async () => {
      try {
        setLoading(true);
        const data = await fetchTeam();
        const apiTeam = Array.isArray(data.team) ? data.team : [];
        const defaultsMap = Object.fromEntries((defaultTeam || []).map(d => [d.slug, d]));
        const normalize = (arr) => (arr || []).map((m) => {
          const d = m && m.slug ? defaultsMap[m.slug] : null;
          const joinedYearNorm = m.joined_year ?? m.joinedYear ?? (d ? (d.joined_year ?? d.joinedYear) : undefined);
          return {
            ...d, // valeurs par défaut d'abord
            ...m, // puis données API pour écraser si présentes
            // normalise les clés pour correspondre au backend/UI
            joined_year: joinedYearNorm,
            image: (m.image && m.image.trim()) ? m.image : (d && d.image ? d.image : ''),
            images: Array.isArray(m.images) && m.images.length > 0 ? m.images : (Array.isArray(d?.images) ? d.images : []),
            videos: Array.isArray(m.videos) && m.videos.length > 0 ? m.videos : (Array.isArray(d?.videos) ? d.videos : []),
            bio: (m.bio && m.bio.trim()) ? m.bio : (d ? d.bio : ''),
            specialties: Array.isArray(m.specialties) && m.specialties.length > 0 ? m.specialties : (Array.isArray(d?.specialties) ? d.specialties : []),
            achievements: Array.isArray(m.achievements) && m.achievements.length > 0 ? m.achievements : (Array.isArray(d?.achievements) ? d.achievements : []),
            vision: Array.isArray(m.vision) && m.vision.length > 0 ? m.vision : (Array.isArray(d?.vision) ? d.vision : []),
            social: m.social || d?.social || {}
          };
        });
        // fallback si l'API renvoie une liste vide
        setTeam(apiTeam.length > 0 ? normalize(apiTeam) : normalize(defaultTeam));
      } catch (e) {
        // en cas d'erreur API, fallback aux données locales
        const normalize = (arr) => (arr || []).map((m) => ({
          ...m,
          joined_year: m.joined_year ?? m.joinedYear ?? m.joined_year,
          images: Array.isArray(m.images) ? m.images : [],
          videos: Array.isArray(m.videos) ? m.videos : [],
          specialties: Array.isArray(m.specialties) ? m.specialties : [],
          achievements: Array.isArray(m.achievements) ? m.achievements : [],
          vision: Array.isArray(m.vision) ? m.vision : [],
          social: m.social || {}
        }));
        setTeam(normalize(defaultTeam));
        setError("");
      } finally { setLoading(false); }
  };

  useEffect(() => { loadTeam(); }, []);

  const handleDeleteMember = async (e, member) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement le profil de ${member.name} ?`)) return;
    try {
      await deleteTeamMember(member.slug, token);
      await loadTeam();
    } catch (err) {
      alert(err.message || 'Erreur lors de la suppression du profil');
    }
  };

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    const titleEl = titleRef.current;
    if (!el || !titleEl) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset;
        const bgTranslate = Math.min(30, y * 0.12); // plus discret
        const titleTranslate = Math.min(16, y * 0.08);
        el.style.transform = `translateY(${bgTranslate}px)`;
        titleEl.style.transform = `translateY(${titleTranslate}px)`;
        ticking = false;
      });
      ticking = true;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!showList) return;
    if (!cardsRef.current) return;
    const options = { threshold: 0.2 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const idxAttr = entry.target.getAttribute('data-index');
        const idx = idxAttr ? parseInt(idxAttr, 10) : -1;
        if (entry.isIntersecting && idx >= 0) {
          setRevealed((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
        }
      });
    }, options);

    cardsRef.current.forEach((node) => node && obs.observe(node));
    return () => obs.disconnect();
  }, [team.length, showList]);

  // Fallback: si l'observer ne s'active pas, révéler toutes les cartes après affichage
  useEffect(() => {
    if (!showList) return;
    if ((revealed || []).length === 0 && team.length > 0) {
      const t = setTimeout(() => {
        setRevealed(Array.from({ length: team.length }, (_, i) => i));
      }, 200);
      return () => clearTimeout(t);
    }
  }, [showList, team.length]);

  const handleMouseMove = (e, index) => {
    if (!isDesktop) return;
    const card = cardsRef.current[index];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const midX = rect.width / 2;
    const midY = rect.height / 2;
    const rotateY = ((x - midX) / midX) * 5;
    const rotateX = -((y - midY) / midY) * 5;
    const t = `perspective(1000px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) scale(1.01)`;
    setTiltTransforms((prev) => ({ ...prev, [index]: t }));
  };

  const handleMouseLeave = (index) => {
    setTiltTransforms((prev) => ({ ...prev, [index]: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)' }));
  };

  const stats = [
    { label: 'Profils', value: String(team.length) },
    { label: 'Année de création', value: '2025' },
    { label: 'Expérience vécue', value: '100%' }
  ];

  return (
    <div className="min-h-screen">
      <div className="relative overflow-hidden">
        <div className="mesh-bg" />
        <div className="grid-overlay" />
        {heroBgUrl ? (
          <div className="absolute inset-0 pointer-events-none">
            <img src={heroBgUrl} alt="Hero" className="w-full h-full object-cover opacity-70" />
          </div>
        ) : (
          <div ref={headerRef} className="absolute inset-0 bg-gradient-to-b from-primary/10 via-secondary/10 to-accent/10 pointer-events-none" />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex items-center justify-end mb-6">
            {isFounder && (
              <Button onClick={() => navigate('/admin/team')} className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Ajouter un profil
              </Button>
            )}
          </div>
          <div ref={titleRef} className="text-center will-change-transform pb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">Notre Équipe</h1>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
              Découvrez les personnes qui font vivre PADAID au quotidien et qui amplifient notre action sur le terrain et en ligne.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setShowList(true);
                  try { listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch(e){}
                }}
                className="inline-flex items-center px-5 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Découvrir l’équipe
              </button>
            </div>
          </div>
        </div>
        <div className="sticky top-16 z-10 bg-background/90 backdrop-blur border-y border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-gradient-animated">{stat.value}</div>
                  <div className="text-muted-foreground text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div ref={listRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {loading && (
          <div className="text-center text-muted-foreground">Chargement…</div>
        )}
        {error && !loading && (
          <div className="text-center text-destructive">{error}</div>
        )}

        {!loading && !error && (showList || team.length > 0) && team.map((member, index) => (
          <Card
            key={member.id || member.slug}
            ref={(el) => (cardsRef.current[index] = el)}
            data-index={index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => handleMouseLeave(index)}
            onMouseMove={(e) => handleMouseMove(e, index)}
            className={`group glow-border border border-border/50 rounded-lg transition-all duration-500 overflow-hidden will-change-transform relative ${
              index % 2 === 1 ? 'bg-muted/20' : ''
            } ${revealed.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'}`}
            style={{ transform: tiltTransforms[index] || 'perspective(1000px)' }}
          >
            {isFounder && (
              <button
                onClick={(e) => handleDeleteMember(e, member)}
                className="absolute top-3 right-3 z-20 p-2 rounded-full bg-background/80 backdrop-blur border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                title="Supprimer ce profil"
                aria-label="Supprimer ce profil"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <CardContent className="p-0">
              <div className="flex flex-col">
                <Link to={`/equipe/${member.slug}`} className="relative overflow-hidden aspect-square">
                  {(() => {
                    const displayImage = member.image || (Array.isArray(member.images) && member.images.length > 0 ? member.images[0] : '');
                    const cacheKey = member.updated_at ? new Date(member.updated_at).getTime() : '';
                    const imgSrc = cacheKey ? `${displayImage}${displayImage.includes('?') ? '&' : '?'}v=${cacheKey}` : displayImage;
                    return displayImage ? (
                      <img
                        src={imgSrc}
                        alt={member.name}
                        className={`w-full h-full object-cover transform transition-transform duration-700 ${hoveredIndex === index ? 'scale-105' : 'scale-100'}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted/30">
                        <span className="text-muted-foreground">Photo à venir</span>
                      </div>
                    );
                  })()}
                  <div className={`pointer-events-none absolute inset-0 transition-opacity duration-700 ${hoveredIndex === index ? 'bg-black/10' : 'bg-black/5'}`} />
                </Link>

                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-1 tracking-tight">
                    <Link to={`/equipe/${member.slug}`} className="hover:underline">
                      {member.name}
                    </Link>
                  </h2>
                  {member.role && (
                    <p className="text-primary font-semibold">{member.role}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Founders;